import React, { useState, useCallback } from 'react';
import { Brain, Play, Volume2, VolumeX, RotateCcw, Save, BookOpen } from 'lucide-react';
import { flashCardLanguages, flashCardDifficulties, flashCardSettings } from '../data/flashCardsData';
import deepseekApi from '../services/deepseekApi';
import FlashCardsFallback from './FlashCardsFallback';
import FlashCardWithAnimation from './FlashCardWithAnimation';
import flashCardStorage from '../services/flashCardStorage';

const FlashCardsGenerator = ({ onBack, selectedEssay = null }) => {
  const [step, setStep] = useState('setup'); // setup, generating, playing, results
  const [formData, setFormData] = useState({
    topic: selectedEssay ? selectedEssay.title : '',
    numberOfCards: flashCardSettings.defaultNumberOfCards,
    language: 'English',
    difficulty: 'intermediate',
    useEssayContent: !!selectedEssay
  });
  const [flashCards, setFlashCards] = useState(null);
  const [error, setError] = useState('');
  const [useAI, setUseAI] = useState(true);
  const [savedCards, setSavedCards] = useState(new Set());

  // Initialize saved cards on component mount
  React.useEffect(() => {
    if (flashCards && flashCards.cards) {
      const savedCardIds = new Set();
      flashCards.cards.forEach(card => {
        const cardId = card.id || flashCardStorage.generateCardId(card);
        if (flashCardStorage.isCardSaved(cardId)) {
          savedCardIds.add(cardId);
        }
      });
      setSavedCards(savedCardIds);
    }
  }, [flashCards]);

  const handleGenerate = async () => {
    if (!formData.topic.trim()) {
      setError('請輸入學習卡片的主題');
      return;
    }

    setError('');
    setStep('generating');

    try {
      const essayContent = formData.useEssayContent && selectedEssay 
        ? `${selectedEssay.content.opening}\n\n${selectedEssay.content.mainBody}\n\n${selectedEssay.content.conclusion}`
        : null;

      const result = await deepseekApi.generateFlashCards(
        formData.topic,
        formData.numberOfCards,
        formData.language,
        formData.difficulty,
        essayContent
      );
      setFlashCards(result);
      setStep('playing');
    } catch (err) {
      console.error('DeepSeek API Error:', err);
      
      // Create educational mock flash cards based on essay content
      const isEssayBased = formData.useEssayContent && selectedEssay;
      const mockFlashCards = {
        title: formData.topic,
        language: formData.language,
        difficulty: formData.difficulty,
        totalCards: formData.numberOfCards,
        cards: isEssayBased ? [
          {
            id: 1,
            question: `"${selectedEssay.title}" 這篇文章的主要目的是什麼？`,
            answer: `這篇文章旨在 ${selectedEssay.content.opening.toLowerCase()}`,
            explanation: `理解文章目的有助於學生掌握作者的意圖和主要訊息。`,
            category: '目的分析',
            keywords: ['目的', '意圖', '主要訊息'],
            imageQuery: selectedEssay.category,
            imageUrl: `https://source.unsplash.com/800x600/?${selectedEssay.category},writing`,
            imageAlt: `${selectedEssay.category} 寫作插圖`
          },
          {
            id: 2,
            question: `這篇 ${selectedEssay.category} 使用了哪些寫作技巧？`,
            answer: `這篇文章使用了正式的問候語、結構化段落和適合 ${selectedEssay.category} 的結尾。`,
            explanation: `識別寫作技巧有助於學生提高自己的寫作技能。`,
            category: '寫作技巧',
            keywords: ['技巧', '結構', '格式'],
            imageQuery: '寫作技巧',
            imageUrl: 'https://source.unsplash.com/800x600/?writing,techniques',
            imageAlt: '寫作技巧插圖'
          },
          {
            id: 3,
            question: `如何針對不同讀者調整這種文章格式？`,
            answer: `您可以改變語調、詞彙水平和例子來配合目標讀者，同時保持相同的結構。`,
            explanation: `學習為不同讀者調整寫作是重要的溝通技能。`,
            category: '適應性',
            keywords: ['適應', '讀者', '溝通'],
            imageQuery: '讀者溝通',
            imageUrl: 'https://source.unsplash.com/800x600/?communication,audience',
            imageAlt: '讀者溝通概念'
          }
        ] : [
          {
            id: 1,
            question: `"${formData.topic}" 的主要議題是什麼？`,
            answer: `主要議題是 ${formData.topic}，涵蓋重要的概念和想法。`,
            explanation: `這個問題幫助學生理解內容的中心主題和重點。`,
            category: '理解',
            keywords: [formData.topic.split(' ')[0], '主要議題', '主題'],
            imageQuery: formData.topic,
            imageUrl: 'https://source.unsplash.com/800x600/?education,learning',
            imageAlt: `關於 ${formData.topic} 的教育內容`
          },
          {
            id: 2,
            question: `"${formData.topic}" 的關鍵概念有哪些？`,
            answer: `關鍵概念包括基本原理、重要術語和實際應用。`,
            explanation: `理解關鍵概念幫助學生掌握主題的基本要素。`,
            category: '分析',
            keywords: ['關鍵概念', '原理', '術語'],
            imageQuery: '關鍵概念',
            imageUrl: 'https://source.unsplash.com/800x600/?concepts,study',
            imageAlt: '關鍵概念插圖'
          },
          {
            id: 3,
            question: `如何在實踐中應用 "${formData.topic}" 的知識？`,
            answer: `您可以通過實際例子、問題解決和實踐練習來應用這些知識。`,
            explanation: `應用問題幫助學生將理論知識與實際技能聯繫起來。`,
            category: '應用',
            keywords: ['應用', '實踐', '實際'],
            imageQuery: '實際應用',
            imageUrl: 'https://source.unsplash.com/800x600/?application,practice',
            imageAlt: '實際應用概念'
          }
        ]
      };
      
      console.log('Using mock flash cards due to API error');
      setFlashCards(mockFlashCards);
      setStep('playing');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveCard = (card) => {
    const cardId = card.id || flashCardStorage.generateCardId(card);
    
    if (savedCards.has(cardId)) {
      flashCardStorage.removeSavedCard(cardId);
      setSavedCards(prev => {
        const newSet = new Set(prev);
        newSet.delete(cardId);
        return newSet;
      });
    } else {
      flashCardStorage.saveCard(card, flashCards);
      setSavedCards(prev => new Set([...prev, cardId]));
    }
  };

  const handleSaveEntireSet = () => {
    if (flashCards) {
      const setId = flashCardStorage.saveFlashCardSet(flashCards);
      if (setId) {
        alert('學習卡片組已成功儲存！');
      } else {
        alert('儲存學習卡片組失敗。');
      }
    }
  };

  // If AI is not available, show fallback component
  if (!useAI) {
    return <FlashCardsFallback onBack={onBack} />;
  }

  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <div
        className="relative rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-pink-600 text-white p-8"
        style={{
          backgroundImage: 'linear-gradient(rgba(147, 51, 234, 0.9), rgba(219, 39, 119, 0.9)), url(https://images.unsplash.com/photo-1606092195730-5d7b9af1efc5?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <Brain className="w-8 h-8" />
            <h2 className="text-3xl font-bold">AI 學習卡片生成器</h2>
          </div>
          <p className="text-purple-100 text-lg">
            使用 AI 技術創建任何主題的個人化學習卡片
          </p>
        </div>
      </div>

      {step === 'setup' && (
        <SetupForm
          formData={formData}
          onInputChange={handleInputChange}
          onGenerate={handleGenerate}
          onBack={onBack}
          error={error}
          onUseSamples={() => setUseAI(false)}
          selectedEssay={selectedEssay}
        />
      )}

      {step === 'generating' && (
        <GeneratingView topic={formData.topic} />
      )}

      {step === 'playing' && flashCards && (
        <FlashCardsPlayer
          flashCards={flashCards}
          onBack={() => setStep('setup')}
          onComplete={() => setStep('results')}
          onSaveCard={handleSaveCard}
          savedCards={savedCards}
          onSaveSet={handleSaveEntireSet}
        />
      )}

      {step === 'results' && flashCards && (
        <ResultsSummary
          flashCards={flashCards}
          onRestart={() => setStep('setup')}
          onPlayAgain={() => setStep('playing')}
        />
      )}
    </div>
  );
};

const SetupForm = ({ formData, onInputChange, onGenerate, onBack, error, onUseSamples, selectedEssay }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold text-gray-900">創建學習卡片</h3>
        <button
          onClick={onBack}
          className="text-gray-500 hover:text-gray-700 transition-colors"
        >
          ← 返回範本
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="text-red-700 mb-3">{error}</div>
          <button
            onClick={onUseSamples}
            className="text-sm bg-red-100 text-red-800 px-3 py-1 rounded hover:bg-red-200 transition-colors"
          >
            嘗試範例學習卡片
          </button>
        </div>
      )}

      {selectedEssay && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <BookOpen className="w-5 h-5 text-blue-600" />
            <h4 className="font-medium text-blue-900">從作文生成</h4>
          </div>
          <p className="text-sm text-blue-700 mb-3">
            基於作文創建學習卡片：<strong>"{selectedEssay.title}"</strong>
          </p>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.useEssayContent}
              onChange={(e) => onInputChange('useEssayContent', e.target.checked)}
              className="rounded border-blue-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-blue-700">使用作文內容生成相關問題</span>
          </label>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            主題 *
          </label>
          <input
            type="text"
            value={formData.topic}
            onChange={(e) => onInputChange('topic', e.target.value)}
            placeholder="例如：中國地理、生物基礎、中文詞彙"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            卡片數量
          </label>
          <select
            value={formData.numberOfCards}
            onChange={(e) => onInputChange('numberOfCards', parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            {[3, 5, 8, 10, 15, 20].map(num => (
              <option key={num} value={num}>{num} 張卡片</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            語言
          </label>
          <select
            value={formData.language}
            onChange={(e) => onInputChange('language', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            {flashCardLanguages.map(lang => (
              <option key={lang.id} value={lang.name}>{lang.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            難度等級
          </label>
          <select
            value={formData.difficulty}
            onChange={(e) => onInputChange('difficulty', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            {flashCardDifficulties.map(diff => (
              <option key={diff.id} value={diff.id}>
                {diff.name} - {diff.description}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <button
          onClick={onGenerate}
          className="flex items-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors font-medium"
        >
          <Brain className="w-4 h-4" />
          生成學習卡片
        </button>
      </div>
    </div>
  );
};

const GeneratingView = ({ topic }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
      <div className="animate-spin w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full mx-auto mb-4"></div>
      <h3 className="text-xl font-semibold text-gray-900 mb-2">
        正在生成學習卡片
      </h3>
      <p className="text-gray-600">
        正在創建關於 "{topic}" 的個人化學習卡片...
      </p>
      <p className="text-sm text-gray-500 mt-2">
        我們的 AI 正在為您精心製作完美的學習體驗，這可能需要一些時間。
      </p>
    </div>
  );
};

const FlashCardsPlayer = ({ flashCards, onBack, onComplete, onSaveCard, savedCards, onSaveSet }) => {
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [autoAdvance, setAutoAdvance] = useState(false);

  const currentCard = flashCards.cards[currentCardIndex];
  const isLastCard = currentCardIndex === flashCards.cards.length - 1;
  const progress = ((currentCardIndex + 1) / flashCards.cards.length) * 100;

  const speakText = useCallback((text) => {
    if (soundEnabled) {
      deepseekApi.speakTextEnhanced(text, flashCards.language);
    }
  }, [soundEnabled, flashCards.language]);

  const handleNext = () => {
    if (isLastCard) {
      onComplete();
    } else {
      setCurrentCardIndex(prev => prev + 1);
      setShowAnswer(false);
    }
  };

  const handlePrevious = () => {
    if (currentCardIndex > 0) {
      setCurrentCardIndex(prev => prev - 1);
      setShowAnswer(false);
    }
  };

  const handleShowAnswer = () => {
    setShowAnswer(true);
    if (autoAdvance) {
      setTimeout(() => {
        handleNext();
      }, flashCardSettings.autoAdvanceDelay);
    }
  };

  React.useEffect(() => {
    if (currentCard && soundEnabled) {
      speakText(currentCard.question);
    }
  }, [currentCardIndex, currentCard, soundEnabled, speakText]);

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="bg-white rounded-xl shadow-sm border p-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              ← 返回設定
            </button>
            <div className="text-sm text-gray-600">
              第 {currentCardIndex + 1} 張，共 {flashCards.cards.length} 張
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSoundEnabled(!soundEnabled)}
              className={`p-2 rounded-lg transition-colors ${
                soundEnabled 
                  ? 'bg-purple-100 text-purple-600' 
                  : 'bg-gray-100 text-gray-400'
              }`}
            >
              {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
            
            <button
              onClick={() => setAutoAdvance(!autoAdvance)}
              className={`p-2 rounded-lg transition-colors ${
                autoAdvance 
                  ? 'bg-purple-100 text-purple-600' 
                  : 'bg-gray-100 text-gray-400'
              }`}
            >
              <Play className="w-4 h-4" />
            </button>
            
            <button
              onClick={onSaveSet}
              className="p-2 rounded-lg bg-green-100 text-green-600 hover:bg-green-200 transition-colors"
              title="儲存整套學習卡片"
            >
              <Save className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        <div className="mt-3">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-purple-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Flash Card */}
      <FlashCardWithAnimation
        card={currentCard}
        showAnswer={showAnswer}
        onShowAnswer={handleShowAnswer}
        onSpeak={speakText}
        soundEnabled={soundEnabled}
        onSave={onSaveCard}
        isSaved={savedCards.has(currentCard.id || flashCardStorage.generateCardId(currentCard))}
      />

      {/* Navigation */}
      <div className="flex justify-between items-center">
        <button
          onClick={handlePrevious}
          disabled={currentCardIndex === 0}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          上一張
        </button>
        
        {showAnswer && (
          <button
            onClick={handleNext}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            {isLastCard ? '完成' : '下一張'}
          </button>
        )}
      </div>
    </div>
  );
};

const ResultsSummary = ({ flashCards, onRestart, onPlayAgain }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
      <div className="mb-6">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Brain className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">
          恭喜您！
        </h3>
        <p className="text-gray-600">
          您已完成所有 {flashCards.totalCards} 張關於 "{flashCards.title}" 的學習卡片
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="text-center">
          <div className="text-2xl font-bold text-purple-600">{flashCards.totalCards}</div>
          <div className="text-sm text-gray-500">已完成卡片</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-600">{flashCards.language}</div>
          <div className="text-sm text-gray-500">語言</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-green-600">{flashCards.difficulty}</div>
          <div className="text-sm text-gray-500">難度</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-orange-600">100%</div>
          <div className="text-sm text-gray-500">進度</div>
        </div>
      </div>

      <div className="flex gap-4 justify-center">
        <button
          onClick={onPlayAgain}
          className="flex items-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors"
        >
          <RotateCcw className="w-4 h-4" />
          再次練習
        </button>
        <button
          onClick={onRestart}
          className="flex items-center gap-2 border border-gray-300 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <Brain className="w-4 h-4" />
          創建新組合
        </button>
      </div>
    </div>
  );
};

export default FlashCardsGenerator; 